import type { AdminUserRow } from '@platewell/schema';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { DataTable, type DataTableColumn } from '../components/data-table.js';
import { AbsoluteDate, RelativeTime } from '../components/relative-time.js';
import { ApiError, listAdminUsers, startImpersonation } from '../lib/api.js';
import { useAdminList } from '../lib/admin-list.js';
import { adminUsersRoute, type AdminListSearch } from '../router.js';

// Module scope so the array is not rebuilt, and every cell renderer, on each render.
const COLUMNS: DataTableColumn<AdminUserRow>[] = [
  {
    key: 'name',
    label: 'Person',
    // Email rides under the name rather than taking a column of its own, which is what
    // keeps this table readable at three columns instead of four.
    render: (row) => (
      <>
        <span className="data-table-strong">{row.name}</span>
        {row.deleted && <span className="data-table-flag">deleted</span>}
        {row.admin && <span className="data-table-flag data-table-flag--note">admin</span>}
        <span className="data-table-subline">{row.email}</span>
      </>
    ),
  },
  {
    key: 'household',
    label: 'Household',
    // A user may belong to any number of households, so all of them are listed. This is the
    // one column with no natural width limit, so it is also the one allowed to wrap.
    wrap: true,
    render: (row) =>
      row.households.length === 0 ? <span className="hint">None</span> : row.households.join(', '),
  },
  {
    key: 'lastActive',
    label: 'Last active',
    render: (row) => <RelativeTime value={row.lastLoggedInAt} />,
  },
  {
    key: 'createdAt',
    label: 'Member since',
    render: (row) => <AbsoluteDate value={row.createdAt} />,
  },
];

/**
 * Why a refusal came back, in the agent's own words.
 *
 * The server's codes are aimed at the client rather than at a person: `target_is_admin`
 * is precise, and it is not a sentence anybody wants to read in a support console.
 */
const IMPERSONATE_REFUSALS: Record<string, string> = {
  target_is_admin: 'Admins cannot be impersonated.',
  target_deleted: 'That account has been deleted.',
  cannot_impersonate_self: 'You are already signed in as yourself.',
  already_impersonating: 'You are already impersonating someone. Exit that first.',
};

export function AdminUsers() {
  const search = adminUsersRoute.useSearch();
  const routeNavigate = useNavigate({ from: '/admin/users' });
  // The row being started, so only its own button reads "Switching…".
  const [switching, setSwitching] = useState<string | null>(null);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);

  const navigate = useCallback(
    (update: (previous: AdminListSearch) => AdminListSearch, replace: boolean) => {
      void routeNavigate({ search: update, replace });
    },
    [routeNavigate],
  );

  const list = useAdminList<AdminUserRow>({
    search,
    defaultSort: 'name',
    fetchPage: listAdminUsers,
    navigate,
    errorLabel: 'Could not load users',
  });

  const impersonate = useCallback((row: AdminUserRow) => {
    setImpersonateError(null);
    setSwitching(row.id);
    // A full load, not a route change: everything below this point — the household
    // manager, its sockets, every parsed document — was built for the admin.
    void startImpersonation(row.id)
      .then(() => window.location.assign('/'))
      .catch((cause: unknown) => {
        const code = cause instanceof ApiError ? cause.code : '';
        setImpersonateError(
          IMPERSONATE_REFUSALS[code] ??
            (cause instanceof Error ? cause.message : 'Could not switch to that account.'),
        );
        setSwitching(null);
      });
  }, []);

  const columns = useMemo<DataTableColumn<AdminUserRow>[]>(
    () => [
      ...COLUMNS,
      {
        key: 'actions',
        label: 'Act as',
        align: 'right',
        // Nothing to order by: the column is a button, not a value.
        sortable: false,
        render: (row) => (
          <button
            type="button"
            className="btn small"
            // Both refusals the server would give anyway, said up front rather than as an
            // error after the click: a deleted account cannot hold a session, and one
            // admin acting as another is how the permission gets worked around.
            disabled={row.deleted || row.admin || switching != null}
            title={row.admin ? 'Admins cannot be impersonated' : undefined}
            aria-label={`Impersonate ${row.name}`}
            onClick={() => impersonate(row)}
          >
            {switching === row.id ? 'Switching…' : 'Impersonate'}
          </button>
        ),
      },
    ],
    [impersonate, switching],
  );

  return (
    <>
      <div className="admin-page-head">
        <h1>Users</h1>
        <span className="admin-range">{list.rangeLabel}</span>
      </div>

      <div className="admin-toolbar">
        <input
          className="search-input"
          type="search"
          aria-label="Search users"
          placeholder="Search name, email, household…"
          value={list.term}
          onChange={(event) => list.setTerm(event.target.value)}
        />
      </div>

      {list.error != null && <p className="error-text">{list.error}</p>}
      {impersonateError != null && <p className="error-text">{impersonateError}</p>}

      <DataTable
        caption="Users"
        columns={columns}
        rows={list.rows}
        rowKey={(row) => row.id}
        total={list.total}
        page={list.page}
        pageSize={list.pageSize}
        sort={list.sort}
        dir={list.dir}
        loading={list.loading}
        onSortChange={list.onSortChange}
        onPageChange={list.onPageChange}
        density="compact"
        rowClassName={(row) => (row.deleted ? 'data-table-row--deleted' : undefined)}
        emptyLabel={list.q === '' ? 'No users yet.' : `Nothing here for “${list.q}”.`}
      />
    </>
  );
}
